/**
 * 格式調整器：偏好設定管理
 *
 * 管理使用者的規則啟用/停用偏好，透過 localStorage 持久化。
 * 供格式調整器頁面和 Notion 表單等多處共用，確保偏好設定一致。
 *
 * === 儲存格式 ===
 *
 * 使用 { ruleId: boolean } map 而非 enabled ID 陣列。
 * 好處：新增規則時自動使用 enabledByDefault，刪除的規則自動忽略，不需版本遷移。
 */

import { rules } from './rules'
import { getDefaultEnabledIds } from './pipeline'

export const STORAGE_KEY = 'text-formatter-settings'

/**
 * 從 localStorage 載入偏好設定，回傳 enabledIds Set。
 *
 * 載入邏輯：有紀錄的規則用紀錄值，沒紀錄的（新規則）用 enabledByDefault。
 * localStorage 不可用或資料損壞時，回傳預設值。
 *
 * @returns {Set<string>} 啟用的規則 ID 集合
 */
export function loadPreferences() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (!stored) return getDefaultEnabledIds()

    const ruleStates = JSON.parse(stored)
    if (typeof ruleStates !== 'object' || ruleStates === null || Array.isArray(ruleStates)) {
      return getDefaultEnabledIds()
    }

    const enabled = new Set()
    for (const rule of rules) {
      // 有儲存紀錄就用紀錄值，沒有就用預設值（新規則不受舊設定影響）
      const isEnabled = rule.id in ruleStates
        ? ruleStates[rule.id]
        : rule.enabledByDefault
      if (isEnabled) enabled.add(rule.id)
    }
    return enabled
  } catch {
    return getDefaultEnabledIds()
  }
}

/**
 * 將 enabledIds 儲存到 localStorage。
 *
 * 儲存所有規則的啟用/停用狀態（不只是 enabled），
 * 確保下次載入時能區分「使用者明確停用」和「新規則尚無紀錄」。
 *
 * @param {Set<string>} enabledIds - 啟用的規則 ID 集合
 */
export function savePreferences(enabledIds) {
  try {
    const ruleStates = {}
    for (const rule of rules) {
      ruleStates[rule.id] = enabledIds.has(rule.id)
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(ruleStates))
  } catch {
    // localStorage 不可用時（如 private browsing）靜默失敗
  }
}

/**
 * 清除 localStorage 中的偏好設定。
 *
 * 用於「重設為預設值」功能。
 */
export function clearPreferences() {
  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // 靜默失敗
  }
}
